import React, { useState, useRef, useEffect, useCallback } from "react";
import { generateAuditedClient } from "../audit/auditedClient";
import {
  CreateUserInput,
  GenerateShareLinkInput,
  getConversationInput,
  ShareLink,
  SubmitQuestion2Input,
  SubmitConversationFeedbackInput,
  GetUserKnowledgeInput,
  Team,
  UpdateTeamUserMapInput,
  User,
  UserKnowledgeSummary,
  Users,
} from "../models/API";
import RequestAccessDialog from "./RequestAccessDialog";
import ShareLinkDialog from "./ShareLinkDialog";
import {
  createUser,
  generateShareLink,
  submitQuestion2,
  updateTeamUserMap,
  submitConversationFeedback,
} from "../graphql/mutations";
import {
  getConversation,
  getTeams,
  getUserKnowledgeSummary,
} from "../graphql/queries";
import ErrorModal from "./ErrorModal";
import MessageModal from "./MessageModal";
import "./ChatBoxComponent.css";
import SendIcon from "@mui/icons-material/Send";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AskAiLanding, {
  SkillAccess,
  SkillTarget,
} from "./askAiLanding/AskAiLanding";
import IosShareIcon from "@mui/icons-material/IosShare";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ReplayIcon from "@mui/icons-material/Replay";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import {
  Box,
  Button,
  Chip,
  Link,
  IconButton,
  Popover,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { marked, parse } from "marked";
import { useTheme } from "@mui/material/styles";
import { hexToString } from "./utils/utils";
import {
  parseStoredResponse,
  StoredResponseError,
} from "./utils/responseMarkers";
import { useGlobal } from "../context/GlobalContext";
import { parseUserAttributes } from "./utils/userAttributes";
import ResourceSelector from "./ResourceSelector";
import PdfUploadButton from "./PdfUploadButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AnswerTableDownloads from "./csvDownload/AnswerTableDownloads";
import { AnswerCharts } from "./answerChart/AnswerChart";
import {
  EXPORTED_CHART_CSS,
  answerChartsElementId,
  exportedChartsHtml,
} from "./answerChart/exportCharts";
import type { ChartSpec } from "./answerChart/types";
import PublishReportDialog, {
  PublishTarget,
} from "./publishedReports/PublishReportDialog";
import { decodeRequestScope } from "./publishedReports/publishedReportModel";

const THINKING_STEPS = [
  "Thinking...",
  "Analyzing your question...",
  "Searching knowledge base...",
  "Querying data sources...",
  "Generating response...",
];

// Parse knowledge ID from S3 source_id like "knowledge/t1/123.txt" or "knowledge/123.txt" (old format)
function parseKnowledgeId(sourceId: string): number | null {
  // New format: knowledge/t{team_id}/{id}.txt or knowledge/t{team_id}/{id}_ref_0.txt
  const newMatch = sourceId.match(/\/t\d+\/(\d+)(?:_ref_\d+)?\.txt$/);
  if (newMatch) return parseInt(newMatch[1], 10);
  // Old format: knowledge/{id}.txt
  const oldMatch = sourceId.match(/(\d+)(?:_ref_\d+)?\.txt$/);
  return oldMatch ? parseInt(oldMatch[1], 10) : null;
}

function ThinkingIndicator() {
  const [elapsed, setElapsed] = useState(0);
  const stepIndex = Math.min(
    Math.floor(elapsed / 5),
    THINKING_STEPS.length - 1
  );

  useEffect(() => {
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <CircularProgress size={16} thickness={5} />
      <span>{THINKING_STEPS[stepIndex]}</span>
      <span style={{ color: "#999", fontSize: "0.85em" }}>({elapsed}s)</span>
    </Box>
  );
}

interface ChatBoxComponentProps {
  isOpen: boolean;
  currentUserName: string;
  userConfig: User | null;
  currentTeamId: number;
  teamConfig: Team;
  conversationId: string;
  menuWidth: number;
  onConversationSaved?: () => void;
  /** The user's effective role in the current team; gates the Publish dialog's visibility options. */
  effectiveRole?: string;
  /** Opens the page behind a FINN Skills card on the landing page. */
  onSkillSelect?: (target: SkillTarget) => void;
  /** Which FINN Skills pages the current team can open. */
  skillAccess?: Partial<Record<SkillTarget, SkillAccess>>;
}
// @ts-ignore
const ChatBoxComponent: React.FC<ChatBoxComponentProps> = ({
  isOpen,
  currentUserName,
  userConfig,
  currentTeamId,
  teamConfig,
  conversationId,
  menuWidth,
  onConversationSaved,
  effectiveRole = "user",
  onSkillSelect,
  skillAccess,
}) => {
  const [showRequestAccessDialog, setShowRequestAccessDialog] = useState(false);
  const [error, setError] = useState<any>(null);
  const [message] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number>(userConfig!.id);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Share link state (share-link spec, tasks 10.1/10.2)
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [sharing, setSharing] = useState(false);
  // Publish-as-report state (publish-report spec, Req 1)
  const [publishTarget, setPublishTarget] = useState<PublishTarget | null>(
    null
  );
  const [messages, setMessages] = useState<
    {
      user: string;
      text: string;
      report: string;
      msgConversationId?: string;
      thinking?: string;
      pdfFilename?: string;
      sources?: any[];
      toolCalls?: any[];
      charts?: ChartSpec[];
      // Provenance stamp of a completed answer (team / model / data-source
      // scope), decoded from the stored turn; undefined on older turns.
      requestScope?: any;
      // Failed turns ("Error" messages): why it failed, and the question the
      // Retry control re-asks.
      error?: StoredResponseError;
      retryQuestion?: string;
    }[]
  >([]);
  const [input, setInput] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [availableSkills, setAvailableSkills] = useState<
    UserKnowledgeSummary[]
  >([]);
  const [knowledgeNameMap, setKnowledgeNameMap] = useState<
    Record<number, string>
  >({});
  const [selectedKnowledgeIds, setSelectedKnowledgeIds] = useState<number[]>(
    []
  );
  const [selectedDataSourceIds, setSelectedDataSourceIds] = useState<number[]>(
    []
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [feedback, setFeedback] = useState<
    Record<number, "up" | "down" | null>
  >({});

  const handleFeedback = async (index: number, value: "up" | "down") => {
    const newValue = feedback[index] === value ? null : value;
    setFeedback((prev) => ({ ...prev, [index]: newValue }));

    // Only call backend if setting feedback (not clearing)
    if (newValue && messages[index]?.msgConversationId) {
      try {
        const feedbackInput: SubmitConversationFeedbackInput = {
          user_id: currentUserId,
          conversation_id: messages[index].msgConversationId!,
          feedback: newValue,
        };
        console.log(
          "[Feedback] Submitting feedback:",
          JSON.stringify(feedbackInput)
        );
        console.log("[Feedback] Message index:", index, "Message data:", {
          user: messages[index].user,
          msgConversationId: messages[index].msgConversationId,
        });
        const result = await client.graphql({
          query: submitConversationFeedback,
          variables: { input: feedbackInput },
        });
        console.log("[Feedback] Backend response:", JSON.stringify(result));
      } catch (err) {
        console.error("[Feedback] Error submitting feedback:", err);
      }
    } else {
      console.log("[Feedback] Skipped backend call:", {
        newValue,
        hasMsgConversationId: !!messages[index]?.msgConversationId,
        msgConversationId: messages[index]?.msgConversationId,
        index,
      });
    }
  };

  const [pdfS3Key, setPdfS3Key] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Use refs to always have the latest values in sendMessage
  const selectedKnowledgeIdsRef = useRef<number[]>([]);
  const selectedDataSourceIdsRef = useRef<number[]>([]);
  const { globalTimeout, showDataSourceSelector, setShowDataSourceSelector } =
    useGlobal();

  // Update refs whenever state changes
  useEffect(() => {
    selectedKnowledgeIdsRef.current = selectedKnowledgeIds;
    selectedDataSourceIdsRef.current = selectedDataSourceIds;
  }, [selectedKnowledgeIds, selectedDataSourceIds]);

  // Fetch available instruction skills and the id -> name map for the sources
  // tooltip. Metadata only (no content): a whole team's content in one
  // response crossed the RDS Data API 1 MiB cap, which emptied the picker and
  // the map for that team.
  useEffect(() => {
    async function fetchSkills() {
      try {
        const input: GetUserKnowledgeInput = {
          user_id: currentUserId,
          team_id: currentTeamId,
        };
        const result = await client.graphql({
          query: getUserKnowledgeSummary,
          variables: { input },
        });
        const allKnowledge = result.data.getUserKnowledge ?? [];
        const skills = allKnowledge.filter(
          (k: UserKnowledgeSummary) => k.knowledge_type === "skill"
        );
        setAvailableSkills(skills);
        // Build knowledge name lookup map (id → name)
        const nameMap: Record<number, string> = {};
        allKnowledge.forEach((k: UserKnowledgeSummary) => {
          nameMap[k.id] = k.knowledge_name;
        });
        setKnowledgeNameMap(nameMap);
      } catch (err) {
        console.error("Failed to fetch skills:", err);
      }
    }
    if (currentUserId && currentTeamId) fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, currentTeamId]);

  // Load historic messages whenever the selected conversation changes so users
  // can resume an earlier session from the history panel. For the current
  // login session (fresh start), getConversation simply returns no items.
  useEffect(() => {
    let cancelled = false;
    async function loadConversationHistory() {
      if (!currentUserId || !conversationId) return;
      try {
        const input: getConversationInput = {
          user_id: currentUserId,
          conversation_id: conversationId,
        };
        const result = await client.graphql({
          query: getConversation,
          variables: { input },
        });
        if (cancelled) return;
        const items = result.data.getConversation ?? [];
        // Preserve chronological order (the resolver already returns items in
        // ascending conversation_id / question_id order).
        const loaded: typeof messages = [];
        items.forEach((q: any) => {
          const questionText = q.question ? hexToString(q.question) : "";
          if (q.question) {
            loaded.push({
              user: "You",
              text: questionText,
              report: "",
              msgConversationId: q.conversation_id,
            });
          }
          if (q.response) {
            const parsed = parseStoredResponse(hexToString(q.response));
            if (parsed.error) {
              // A failed turn renders as an error with a Retry control, not
              // as an answer.
              loaded.push({
                user: "Error",
                text: parsed.text,
                report: "",
                msgConversationId: q.conversation_id,
                error: parsed.error,
                retryQuestion: questionText || undefined,
              });
            } else {
              loaded.push({
                user: "AI",
                text: parsed.text + "\n",
                report: "",
                msgConversationId: q.conversation_id,
                thinking: q.thinking ? hexToString(q.thinking) : undefined,
                sources: parsed.sources,
                toolCalls: parsed.toolCalls,
                charts: parsed.charts,
                requestScope: decodeRequestScope(hexToString, q.request_scope),
              });
            }
          }
        });
        setMessages(loaded);
      } catch (err) {
        console.error("[ChatBox] Failed to load conversation history:", err);
      }
    }
    loadConversationHistory();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, currentUserId]);

  const handleSelectionChange = useCallback(
    (knowledgeIds: number[], dataSourceIds: number[]) => {
      setSelectedKnowledgeIds(knowledgeIds);
      setSelectedDataSourceIds(dataSourceIds);
    },
    []
  );

  const handlePdfUploaded = useCallback((s3Key: string, filename: string) => {
    setPdfS3Key(s3Key);
    setPdfFilename(filename);
  }, []);

  const handlePdfRemoved = useCallback(() => {
    setPdfS3Key(null);
    setPdfFilename(null);
  }, []);

  const handleDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // The Share control is enabled only while the loaded conversation has at
  // least one completed (non-streaming) AI turn (Req 1.1, 1.2). Completed AI
  // turns are rendered as "AI" messages; in-flight turns use the "Placeholder"
  // role and are gated out by `isLoading`.
  const hasCompletedTurn = !isLoading && messages.some((m) => m.user === "AI");

  // Open the Publish dialog for the completed answer at `index` (publish-report
  // spec, Req 1.4). The turn's question is the nearest preceding "You" message:
  // both the history loader and the live path keep question and answer adjacent.
  const handlePublishClick = (index: number) => {
    const msg = messages[index];
    if (!msg || msg.user !== "AI" || !msg.msgConversationId || isLoading)
      return;
    let question = "";
    let pdf = false;
    for (let i = index - 1; i >= 0; i -= 1) {
      if (messages[i].user === "You") {
        question = messages[i].text;
        // An answer produced from an attached PDF cannot be re-run from its
        // question alone, so the dialog publishes it as a snapshot (F2, Req 2.7).
        pdf = !!messages[i].pdfFilename;
        break;
      }
    }
    setPublishTarget({
      conversationId: msg.msgConversationId,
      question,
      markdown: msg.text,
      toolCalls: msg.toolCalls,
      sources: msg.sources,
      charts: msg.charts,
      requestScope: msg.requestScope,
      pdf,
    });
  };

  // Generate (or fetch the existing) Share_Link for the current conversation
  // and open the ShareLinkDialog (Req 1.3, 1.8).
  const handleShareClick = async () => {
    if (!hasCompletedTurn || sharing) return;
    setSharing(true);
    try {
      const input: GenerateShareLinkInput = {
        conversation_id: conversationId,
        user_id: currentUserId,
      };
      const result = await client.graphql({
        query: generateShareLink,
        variables: { input },
      });
      const link = result.data.generateShareLink;
      if (!link) {
        throw new Error("No share link returned");
      }
      setShareLink(link);
      setShareDialogOpen(true);
    } catch (err) {
      console.error("[ChatBox] Failed to generate share link:", err);
      setError(err);
    } finally {
      setSharing(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };
  const theme = useTheme();
  const { env } = useGlobal();
  const watermark = env.toUpperCase();
  const feedbackUrl =
    "https://form.asana.com/?k=YBdjTTPowR_mgOBaKXL7qQ&d=8442528107068";
  let client = generateAuditedClient();

  useEffect(() => {
    scrollToBottom();
    setCurrentUserId(userConfig!.id);
  }, [messages, userConfig]);

  // Load data source selector preference from user attributes
  useEffect(() => {
    if (userConfig) {
      const attrs = parseUserAttributes(userConfig);
      setShowDataSourceSelector(attrs.show_data_source_selector ?? false);
    }
  }, [userConfig, setShowDataSourceSelector]);

  const resetTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "4rem";
    }
  };

  // Landing-page example cards prefill the composer so the user can review
  // or tweak the prompt before sending.
  const handleExampleSelect = (prompt: string) => {
    setInput(prompt);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.focus();
      requestAnimationFrame(() => {
        textarea.setSelectionRange(prompt.length, prompt.length);
        handleTextarea();
      });
    }
  };

  const handleTextarea = () => {
    if (textareaRef.current) {
      resetTextarea();
      textareaRef.current.style.height = `${Math.max(
        4,
        Math.min(textareaRef.current.scrollHeight / 16, 9.4)
      )}rem`;
    }
  };

  // Export function to download AI response as HTML (answer only)
  const exportAsHTML = (msg: any, index: number) => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>FINN AI Response - ${new Date().toLocaleDateString()}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
            max-width: 900px;
            margin: 40px auto;
            padding: 20px;
            line-height: 1.6;
            color: #333;
            background-color: #f5f5f5;
        }
        .container {
            background-color: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .header {
            border-bottom: 2px solid #0073bb;
            padding-bottom: 15px;
            margin-bottom: 25px;
        }
        .header h1 {
            margin: 0;
            color: #0073bb;
            font-size: 24px;
        }
        .metadata {
            color: #666;
            font-size: 14px;
            margin-top: 8px;
        }
        .content {
            margin-top: 20px;
        }
        .answer {
            padding: 15px 0;
        }
        table {
            border-collapse: collapse;
            width: 100%;
            margin: 15px 0;
        }
        th, td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
        }
        th {
            background-color: #0073bb;
            color: white;
            font-weight: 600;
        }
        tr:nth-child(even) {
            background-color: #f9f9f9;
        }
        code {
            background-color: #f4f4f4;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
        }
        pre {
            background-color: #f4f4f4;
            padding: 15px;
            border-radius: 4px;
            overflow-x: auto;
        }
${EXPORTED_CHART_CSS}
        .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 12px;
            color: #666;
            text-align: center;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🤖 FINN AI Assistant Response</h1>
            <div class="metadata">
                <div><strong>Date:</strong> ${new Date().toLocaleString()}</div>
                <div><strong>User:</strong> ${currentUserName}</div>
                <div><strong>Conversation ID:</strong> ${conversationId}</div>
            </div>
        </div>
        
        <div class="content">
            <div class="answer">
                ${parse(msg.text) as string}
            </div>
            ${exportedChartsHtml(index)}
        </div>
        
        <div class="footer">
            Generated by FINN - SPB Finance AI Assistant | Amazon ${env.toUpperCase()} Environment
        </div>
    </div>
</body>
</html>
    `;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `FINN_Response_${
      new Date().toISOString().split("T")[0]
    }_${index}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Poll until the turn for `questionId` is stored, then render it. Resolves
  // true for an answer and false for a failed turn, which renders as an Error
  // message whose Retry control re-asks `questionText`.
  async function startPollingQuestion(
    questionId: string,
    questionText: string
  ): Promise<boolean> {
    const INTERVAL_MS = 2000; // ms
    const MAX_ATTEMPTS = (globalTimeout * 1000) / INTERVAL_MS;

    let attempts = 0;

    while (attempts < MAX_ATTEMPTS) {
      await new Promise((r) => setTimeout(r, INTERVAL_MS));
      attempts += 1;

      try {
        const getConversationInput: getConversationInput = {
          user_id: currentUserId,
          conversation_id: questionId,
        };
        const result = await client.graphql({
          query: getConversation,
          variables: {
            input: getConversationInput,
          },
        });

        const res = result.data.getConversation;
        if (!res || res.length === 0) continue;
        let botMessage: any = null;
        let answered = true;

        if (res[0].response) {
          // Strips the hidden SOURCES / CHARTS / TOOLCALLS / ERROR markers.
          const parsed = parseStoredResponse(hexToString(res[0].response));
          if (parsed.error) {
            answered = false;
            botMessage = {
              user: "Error",
              text: parsed.text,
              report: "",
              msgConversationId: res[0].conversation_id,
              error: parsed.error,
              retryQuestion: questionText,
            };
          } else {
            botMessage = {
              user: "AI",
              text: parsed.text + "\n",
              report: "",
              msgConversationId: res[0].conversation_id,
              thinking: res[0].thinking
                ? hexToString(res[0].thinking)
                : undefined,
              sources: parsed.sources,
              toolCalls: parsed.toolCalls,
              charts: parsed.charts,
              requestScope: decodeRequestScope(
                hexToString,
                res[0].request_scope
              ),
            };
          }
        } else {
          botMessage = {
            user: "AI",
            text:
              "Sorry, I couldn't generate a response for this request because the required data is unavailable or the system encountered an issue. Please try again later." +
              "\n",
            report: "",
          };
        }
        setMessages((prev) => prev.slice(0, -1).concat(botMessage));
        setIsLoading(false);
        onConversationSaved?.();
        return answered;
      } catch (e) {
        console.error("polling error", e);
        // optional: break or continue depending on your tolerance
      }
    }
    throw new Error("Timeout");
  }

  // Ask one question and render its answer. Shared by the input box and the
  // Retry control on a failed turn, so a retry re-asks with the same scope
  // (team, knowledge, data sources, skills) and the still-attached PDF.
  const submitQuestion = async (questionText: string) => {
    setIsLoading(true);

    const userMessage = {
      user: "You",
      text: questionText,
      report: "",
      msgConversationId: undefined as string | undefined,
      pdfFilename: pdfFilename || undefined,
    };
    const placeholderMessage = {
      user: "Placeholder",
      text: "Thinking...",
      report: "",
      msgConversationId: undefined as string | undefined,
    };
    setMessages((prev) => [...prev, userMessage, placeholderMessage]);
    let botMessage: any = null;
    try {
      const submitQuestionInput: SubmitQuestion2Input = {
        user_id: currentUserId,
        team_id: currentTeamId,
        conversation_id: conversationId,
        question: questionText,
        user_name: currentUserName,
        knowledge_base_ids: selectedKnowledgeIdsRef.current,
        // When data source selector is hidden, pass undefined (= load ALL tools).
        // An empty array [] means "explicitly load no tools".
        data_source_ids: showDataSourceSelector
          ? selectedDataSourceIdsRef.current
          : undefined,
        ...(pdfS3Key && { pdf_s3_key: pdfS3Key }),
        ...(pdfFilename && { pdf_filename: pdfFilename }),
        ...(selectedSkills.length > 0 && { skills: selectedSkills }),
      };
      console.log("Submit [%o] to AI v2", submitQuestionInput);
      const result = await client.graphql({
        query: submitQuestion2,
        variables: {
          input: submitQuestionInput,
        },
      });
      console.log("Got the response [%o] from AI v2", result);
      const response = result.data.submitQuestion2;
      if (response.success) {
        // Keep the PDF attached when the turn failed, so Retry re-asks with it.
        if (await startPollingQuestion(response.question_id, questionText)) {
          setPdfS3Key(null);
          setPdfFilename(null);
        }
      } else {
        botMessage = {
          user: "AI",
          text: response.message + "\n",
          report: "",
          msgConversationId: undefined as string | undefined,
        };
        setMessages((prev) => prev.slice(0, -1).concat(botMessage));
        setIsLoading(false);
      }
    } catch (error) {
      const errorMessage =
        (error as any)?.errors?.[0]?.message ?? "An unexpected error occurred";
      botMessage = {
        user: "Error",
        text:
          "Oops... I ran into some problems. Error message: " +
          errorMessage +
          "\n",
        report: "",
        msgConversationId: undefined as string | undefined,
        retryQuestion: questionText,
      };
      console.error("Error calling Lambda:", error);
      setMessages((prev) => prev.slice(0, -1).concat(botMessage));
      setIsLoading(false);
      return null;
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const questionText = input;
    setInput("");
    await submitQuestion(questionText);
  };

  // Re-ask the question of a failed turn as a new turn (the failed turn stays
  // in the stored history, so it stays in the transcript too).
  const handleRetry = (questionText: string) => {
    if (isLoading) return;
    submitQuestion(questionText);
  };

  marked.setOptions({
    breaks: true,
    gfm: true,
  });

  const renderMessage = (msg: any, index: number) => {
    if (msg.user === "Placeholder") {
      return <ThinkingIndicator />;
    }
    if (msg.user !== "AI") {
      return (
        <>
          {msg.pdfFilename && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mb: 1,
                p: "6px 10px",
                backgroundColor: "rgba(0, 115, 187, 0.08)",
                borderRadius: "6px",
                border: "1px solid rgba(0, 115, 187, 0.2)",
                width: "fit-content",
              }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 18, color: "#d32f2f" }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: "#333",
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {msg.pdfFilename}
              </Typography>
            </Box>
          )}
          {msg.text}
        </>
      );
    }
    // The answer HTML is produced exactly as before; the component that
    // receives it owns the injection sink and adds one "Download CSV" control
    // per table it finds (ask-ai-csv-download, Req 1.1, 1.5, 2.1, 4.1).
    // Charts from the create_chart tool render under the answer text; the
    // table above keeps the exact numbers and its CSV download.
    return (
      <>
        <AnswerTableDownloads
          html={(parse(msg.text) as string).replace(/>\s+</g, "><")}
          messageIndex={index}
          conversationId={conversationId}
        />
        <div id={answerChartsElementId(index)}>
          <AnswerCharts charts={msg.charts} />
        </div>
      </>
    );
  };

  async function fetchTeamsData() {
    try {
      const teamsData = await client.graphql({
        query: getTeams,
      });
      const teams = teamsData.data.getTeams;
      setTeams(teams);

      const createUserInput: CreateUserInput = {
        user_name: currentUserName,
      };
      const userData = await client.graphql({
        query: createUser,
        variables: {
          input: createUserInput,
        },
      });
      const currentUserId = userData.data.createUser.id;
      setCurrentUserId(currentUserId);
    } catch (err) {
      console.error(err);
    }
  }

  const openRequestAccessDialog = async () => {
    await fetchTeamsData();
    setShowRequestAccessDialog(true);
  };

  const handleRequestAccessClose = () => {
    setShowRequestAccessDialog(false);
  };

  const handleRequestAccessSave = async (item: Users) => {
    try {
      const teamUserMap: UpdateTeamUserMapInput = {
        team_id: currentTeamId,
        user_ids: item.user_ids.join(),
      };
      await client.graphql({
        query: updateTeamUserMap,
        variables: {
          input: teamUserMap,
        },
      });
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  if (currentTeamId === 0)
    return (
      isOpen && (
        <div>
          <div>You don't have permission to access any team's data.</div>
          <button onClick={openRequestAccessDialog}>Request Access</button>
          <RequestAccessDialog
            isOpen={showRequestAccessDialog}
            onSave={handleRequestAccessSave}
            onClose={handleRequestAccessClose}
            currentUserId={currentUserId}
            initialData={teams}
            currentUserName={currentUserName}
          />
        </div>
      )
    );
  return (
    isOpen && (
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: `${menuWidth}px`,
          height: "100vh",
          overflowY: "hidden",
          // Warm canvas behind the Ask AI landing page (light mode only).
          bgcolor:
            messages.length === 0 && theme.palette.mode !== "dark"
              ? "#f8f6f1"
              : undefined,
          "::before": {
            content: `"${watermark}"`,
            position: "absolute",
            top: "0",
            left: "middle",
            fontSize: "6.2rem",
            fontWeight: "800",
            color:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.18)"
                : "#000000d1",
            zIndex: "1",
            pointerEvents: "none",
          },
          transition: theme.transitions.create("margin-left", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <div className={`ChatBoxComponent-panel`}>
          {messages.length === 0 ? (
            /* Landing page for the initial (empty) state */
            <AskAiLanding
              isDark={theme.palette.mode === "dark"}
              onExampleSelect={handleExampleSelect}
              onSkillSelect={onSkillSelect}
              skillAccess={skillAccess}
            >
              {/* Input Container */}
              <div className="ChatBoxComponent-input-container">
                {/* Skill Selector Chips */}
                {availableSkills.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}
                  >
                    {availableSkills.map((skill) => (
                      <Chip
                        key={skill.id}
                        label={skill.knowledge_name}
                        size="small"
                        variant={
                          selectedSkills.includes(skill.knowledge_name)
                            ? "filled"
                            : "outlined"
                        }
                        color={
                          selectedSkills.includes(skill.knowledge_name)
                            ? "primary"
                            : "default"
                        }
                        onClick={() =>
                          setSelectedSkills((prev) =>
                            prev.includes(skill.knowledge_name)
                              ? prev.filter((s) => s !== skill.knowledge_name)
                              : [...prev, skill.knowledge_name]
                          )
                        }
                        sx={{ cursor: "pointer" }}
                      />
                    ))}
                  </Box>
                )}
                <div className="ChatBoxComponent-input">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      handleTextarea();
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        !e.metaKey &&
                        !e.ctrlKey &&
                        !isLoading
                      ) {
                        e.preventDefault();
                        sendMessage();
                        resetTextarea();
                      }
                    }}
                    placeholder="Ask anything about SPB Finance data, e.g. why did US SP Search RPOM slow down?"
                  />
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <PdfUploadButton
                      userId={currentUserId}
                      onPdfUploaded={handlePdfUploaded}
                      onPdfRemoved={handlePdfRemoved}
                      disabled={isLoading}
                    />
                    {showDataSourceSelector && (
                      <IconButton
                        onClick={handleDropdownClick}
                        disabled={isLoading}
                        size="small"
                        sx={{
                          color: isLoading ? "gray" : "inherit",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    )}
                    <button
                      className={"ChatBoxComponent-button"}
                      aria-label="Send"
                      onClick={() => {
                        sendMessage();
                        resetTextarea();
                      }}
                      disabled={isLoading || !input.trim()}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <ArrowForwardIcon />
                      )}
                    </button>
                  </div>
                </div>
                {/* Popover for ResourceSelector */}
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleDropdownClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Box sx={{ p: 2, minWidth: "300px" }}>
                    <ResourceSelector
                      userId={currentUserId}
                      onSelectionChange={handleSelectionChange}
                    />
                  </Box>
                </Popover>
                {/* Footer */}
                <div className="ChatBoxComponent-footer">
                  <div
                    style={{
                      margin: "auto 0",
                      fontSize: "0.8rem",
                    }}
                  >
                    Enter to send · Shift+Enter for a new line
                  </div>
                  <Link
                    href={feedbackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="AskAiLanding-feedback"
                  >
                    Give feedback
                  </Link>
                </div>
              </div>
            </AskAiLanding>
          ) : (
            /* Layout with messages */
            <>
              {/* Chat header / toolbar (share-link spec, task 10.1) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  px: 1,
                  pb: 1,
                  zIndex: 2,
                }}
              >
                <Tooltip
                  title={
                    hasCompletedTurn
                      ? "Share this conversation"
                      : "Share is available once the conversation has a completed response"
                  }
                >
                  <span>
                    <Button
                      size="small"
                      onClick={handleShareClick}
                      disabled={!hasCompletedTurn || sharing}
                      startIcon={
                        sharing ? (
                          <CircularProgress size={16} />
                        ) : (
                          <IosShareIcon fontSize="small" />
                        )
                      }
                    >
                      Share
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              {/* Message Container */}
              <div className="ChatBoxComponent-message-container">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`ChatBoxComponent-message-wrapper ${msg.user}`}
                  >
                    <div
                      className={`ChatBoxComponent-message ${msg.user}`}
                      data-dark={
                        theme.palette.mode === "dark" ? "true" : undefined
                      }
                      style={
                        theme.palette.mode === "dark"
                          ? {
                              backgroundColor:
                                msg.user === "You"
                                  ? "rgba(33,150,243,0.18)"
                                  : msg.user === "Error"
                                  ? "rgba(211,47,47,0.28)"
                                  : theme.palette.background.paper,
                              color: theme.palette.text.primary,
                            }
                          : undefined
                      }
                    >
                      {/* Thinking accordion for AI messages */}
                      {msg.user === "AI" && msg.thinking && (
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            mb: 1,
                            backgroundColor: "rgba(0, 0, 0, 0.03)",
                            borderRadius: "8px",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: 36, py: 0 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#666", fontStyle: "italic" }}
                            >
                              💭 View AI thinking
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#555",
                                whiteSpace: "pre-wrap",
                                fontSize: "0.85rem",
                              }}
                            >
                              {msg.thinking}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      )}
                      {renderMessage(msg, index)}
                      {/* Failed turn: re-ask the same question */}
                      {msg.user === "Error" &&
                        (msg.retryQuestion || msg.error) && (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            {msg.retryQuestion && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                startIcon={<ReplayIcon fontSize="small" />}
                                disabled={isLoading}
                                onClick={() => handleRetry(msg.retryQuestion!)}
                                aria-label="Retry this question"
                                sx={{ textTransform: "none" }}
                              >
                                Retry
                              </Button>
                            )}
                            {msg.error && (
                              <Typography
                                variant="caption"
                                sx={{ opacity: 0.8 }}
                              >
                                Error: {msg.error.type}
                              </Typography>
                            )}
                          </Box>
                        )}
                      {/* RAG Source Citations */}
                      {msg.user === "AI" &&
                        msg.sources &&
                        msg.sources.length > 0 && (
                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              mt: 1,
                              backgroundColor: "rgba(0, 115, 187, 0.04)",
                              borderRadius: "8px",
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ minHeight: 36, py: 0 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#0073bb", fontWeight: 500 }}
                              >
                                📚 Sources ({msg.sources.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {msg.sources.map((src: any, i: number) => {
                                const kid = parseKnowledgeId(
                                  src.source_id || ""
                                );
                                const name = kid
                                  ? knowledgeNameMap[kid]
                                  : undefined;
                                return (
                                  <Tooltip
                                    key={i}
                                    arrow
                                    placement="top"
                                    title={
                                      <Box sx={{ maxWidth: 320, p: 0.5 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            display: "block",
                                            mb: 0.5,
                                          }}
                                        >
                                          {name || `Knowledge #${kid || i + 1}`}
                                        </Typography>
                                        {src.chunk_excerpt && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              display: "block",
                                              color: "rgba(255,255,255,0.85)",
                                              mb: 0.5,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            {src.chunk_excerpt}...
                                          </Typography>
                                        )}
                                        {src.relevance_score != null && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "rgba(255,255,255,0.7)",
                                            }}
                                          >
                                            Relevance:{" "}
                                            {(
                                              Number(src.relevance_score) * 100
                                            ).toFixed(0)}
                                            %
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  >
                                    <Chip
                                      label={`#${kid || i + 1}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        mr: 0.5,
                                        mb: 0.5,
                                        height: "22px",
                                        fontSize: "0.7rem",
                                        borderColor: "#0073bb",
                                        color: "#0073bb",
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(0, 115, 187, 0.08)",
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      {/* Tool Calls */}
                      {msg.user === "AI" &&
                        msg.toolCalls &&
                        msg.toolCalls.length > 0 && (
                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              mt: 1,
                              backgroundColor: "rgba(156, 39, 176, 0.04)",
                              borderRadius: "8px",
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ minHeight: 36, py: 0 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#9c27b0", fontWeight: 500 }}
                              >
                                🔧 Tool Calls ({msg.toolCalls.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {msg.toolCalls.map((tc: any, i: number) => (
                                <Accordion
                                  key={i}
                                  disableGutters
                                  elevation={0}
                                  sx={{
                                    mb: 0.5,
                                    backgroundColor: "rgba(0,0,0,0.02)",
                                    borderRadius: "4px",
                                    "&:before": { display: "none" },
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ minHeight: 28, py: 0, px: 1 }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#9c27b0",
                                      }}
                                    >
                                      {tc.name}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ pt: 0, px: 1 }}>
                                    {tc.input && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: "#555",
                                            display: "block",
                                            mb: 0.25,
                                          }}
                                        >
                                          Input:
                                        </Typography>
                                        <Box
                                          component="pre"
                                          sx={{
                                            fontSize: "0.7rem",
                                            backgroundColor: "rgba(0,0,0,0.04)",
                                            p: 0.5,
                                            borderRadius: "4px",
                                            overflow: "auto",
                                            maxHeight: 150,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-all",
                                            m: 0,
                                          }}
                                        >
                                          {typeof tc.input === "string"
                                            ? tc.input
                                            : JSON.stringify(tc.input, null, 2)}
                                        </Box>
                                      </Box>
                                    )}
                                    {tc.result && (
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: "#555",
                                            display: "block",
                                            mb: 0.25,
                                          }}
                                        >
                                          Result:
                                        </Typography>
                                        <Box
                                          component="pre"
                                          sx={{
                                            fontSize: "0.7rem",
                                            backgroundColor: "rgba(0,0,0,0.04)",
                                            p: 0.5,
                                            borderRadius: "4px",
                                            overflow: "auto",
                                            maxHeight: 200,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-all",
                                            m: 0,
                                          }}
                                        >
                                          {tc.result}
                                        </Box>
                                      </Box>
                                    )}
                                    {/* Fallback for old format with input_summary */}
                                    {!tc.input && tc.input_summary && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#666",
                                          wordBreak: "break-all",
                                        }}
                                      >
                                        {tc.input_summary}
                                      </Typography>
                                    )}
                                  </AccordionDetails>
                                </Accordion>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      {/* Feedback and export buttons for AI messages */}
                      {msg.user === "AI" && (
                        <div
                          style={{
                            marginTop: "10px",
                            textAlign: "right",
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "4px",
                          }}
                        >
                          <Tooltip title="Good response">
                            <IconButton
                              size="small"
                              onClick={() => handleFeedback(index, "up")}
                              sx={{
                                color:
                                  feedback[index] === "up" ? "#2e7d32" : "#888",
                                "&:hover": {
                                  backgroundColor: "rgba(46, 125, 50, 0.1)",
                                },
                              }}
                            >
                              {feedback[index] === "up" ? (
                                <ThumbUpIcon fontSize="small" />
                              ) : (
                                <ThumbUpOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Bad response">
                            <IconButton
                              size="small"
                              onClick={() => handleFeedback(index, "down")}
                              sx={{
                                color:
                                  feedback[index] === "down"
                                    ? "#d32f2f"
                                    : "#888",
                                "&:hover": {
                                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                                },
                              }}
                            >
                              {feedback[index] === "down" ? (
                                <ThumbDownIcon fontSize="small" />
                              ) : (
                                <ThumbDownOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Export as HTML">
                            <IconButton
                              size="small"
                              onClick={() => exportAsHTML(msg, index)}
                              sx={{
                                color: "#0073bb",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 115, 187, 0.1)",
                                },
                              }}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/* Publish as a report: only for stored answers (an
                              id is what the report points back to), and never
                              while a question is in flight (Req 1.1-1.3). */}
                          {msg.msgConversationId && (
                            <Tooltip title="Publish as a dashboard report">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handlePublishClick(index)}
                                  disabled={isLoading}
                                  aria-label="Publish as a dashboard report"
                                  sx={{
                                    color: "#0073bb",
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 115, 187, 0.1)",
                                    },
                                  }}
                                >
                                  <PublishOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Container */}
              <div className="ChatBoxComponent-input-container">
                {/* Skill Selector Chips */}
                {availableSkills.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}
                  >
                    {availableSkills.map((skill) => (
                      <Chip
                        key={skill.id}
                        label={skill.knowledge_name}
                        size="small"
                        variant={
                          selectedSkills.includes(skill.knowledge_name)
                            ? "filled"
                            : "outlined"
                        }
                        color={
                          selectedSkills.includes(skill.knowledge_name)
                            ? "primary"
                            : "default"
                        }
                        onClick={() =>
                          setSelectedSkills((prev) =>
                            prev.includes(skill.knowledge_name)
                              ? prev.filter((s) => s !== skill.knowledge_name)
                              : [...prev, skill.knowledge_name]
                          )
                        }
                        sx={{ cursor: "pointer" }}
                      />
                    ))}
                  </Box>
                )}
                <div className="ChatBoxComponent-input">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      handleTextarea();
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        !e.metaKey &&
                        !e.ctrlKey &&
                        !isLoading
                      ) {
                        e.preventDefault();
                        sendMessage();
                        resetTextarea();
                      }
                    }}
                    placeholder="Ask me anything about SPB Finance data"
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      position: "absolute",
                      right: "1rem",
                      bottom: "50%",
                      transform: "translateY(50%)",
                      alignItems: "center",
                    }}
                  >
                    <PdfUploadButton
                      userId={currentUserId}
                      onPdfUploaded={handlePdfUploaded}
                      onPdfRemoved={handlePdfRemoved}
                      disabled={isLoading}
                    />
                    {showDataSourceSelector && (
                      <IconButton
                        onClick={handleDropdownClick}
                        disabled={isLoading}
                        size="small"
                        sx={{
                          color: isLoading ? "gray" : "inherit",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    )}
                    <button
                      className={"ChatBoxComponent-button"}
                      onClick={() => {
                        sendMessage();
                        resetTextarea();
                      }}
                      disabled={isLoading || !input.trim()}
                      style={{ position: "static", transform: "none" }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <SendIcon />
                      )}
                    </button>
                  </div>
                </div>
                {/* Popover for ResourceSelector */}
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleDropdownClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Box sx={{ p: 2, minWidth: "300px" }}>
                    <ResourceSelector
                      userId={currentUserId}
                      onSelectionChange={handleSelectionChange}
                    />
                  </Box>
                </Popover>
                {/* Footer */}
                <div className="ChatBoxComponent-footer">
                  <div
                    style={{
                      margin: "auto 0",
                      fontSize: "0.8rem",
                    }}
                  >
                    Press Enter to send, Shift+Enter for new line
                  </div>
                  <Link
                    href={feedbackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      fontSize: "1.2rem",
                      fontWeight: "600",
                      color: "blue",
                      opacity: "0.8",
                      "&:hover": {
                        opacity: "1",
                      },
                    }}
                  >
                    Feedback
                  </Link>
                </div>
              </div>
            </>
          )}
          <ErrorModal error={error} onClose={() => setError(null)} />
          <MessageModal message={message} />
          <ShareLinkDialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            shareLink={shareLink}
            userId={currentUserId}
            onUpdated={(updated) => setShareLink(updated)}
          />
          <PublishReportDialog
            open={publishTarget !== null}
            onClose={() => setPublishTarget(null)}
            userId={currentUserId}
            teamId={currentTeamId}
            teamName={teamConfig?.name ?? ""}
            effectiveRole={effectiveRole}
            target={publishTarget}
            runTimeoutSeconds={globalTimeout}
          />
        </div>
      </Box>
    )
  );
};

export default ChatBoxComponent;
